import { NgIf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDialogActions, MatDialogContent } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EulaDialogData } from '@models';
import { EulaService } from '@services';
import { finalize, fromEvent, Subject, takeUntil, throttleTime } from 'rxjs';

@Component({
  selector: 'app-eula-dialog',
  templateUrl: './eula-dialog.component.html',
  styleUrls: ['./eula-dialog.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.ShadowDom,
  imports: [NgIf, MatDialogContent, MatDialogActions]
})
export class EulaDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild('eulaContent', { static: false }) eulaContentRef!: ElementRef<HTMLElement>;

  sanitizedContent: SafeHtml;
  title: string;
  isSavingEula = false;
  eulaId: number;
  usePostMethod = false;
  hasReadFullDocument = false;

  private readonly dialogRef = inject(MatDialogRef<EulaDialogComponent>);
  private readonly data = inject<EulaDialogData>(MAT_DIALOG_DATA);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly eulaService = inject(EulaService);
  private readonly destroy$ = new Subject<void>();

  constructor() {
    const decoded = this.decodeEscapedHtml(this.data.eulaContent);
    const normalized = this.normalizeHtml(decoded);

    this.sanitizedContent = this.sanitizer.bypassSecurityTrustHtml(normalized);

    this.title = this.data.eulaTitle;
    this.eulaId = this.data.eulaId;
    this.usePostMethod = this.data.usePost;
  }

  ngAfterViewInit(): void {
    if (this.eulaContentRef?.nativeElement) {
      this.setupScrollTracking();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onAccept(): void {
    this.isSavingEula = true;
    const methodToBeUsed = this.usePostMethod ? 'saveEulaAcceptance' : 'saveEulaAcceptanceFromGet';
    this.eulaService[methodToBeUsed](this.eulaId)
      .pipe(finalize(() => (this.isSavingEula = false)))
      .subscribe({
        next: (response) => {
          if (response.acceptedAt) {
            this.eulaService.storeEulaAcceptance(true);
          }
          this.dialogRef.close(true);
        },
        error: () => {
          // Error handling - finalize will set isSavingEula to false
        }
      });
  }

  get isAcceptButtonEnabled(): boolean {
    return this.hasReadFullDocument && !this.isSavingEula;
  }

  private decodeEscapedHtml(html: string): string {
    if (!html) return '';

    const decoded = html.replace(/\\n/g, '\n').replace(/\\"/g, '"');

    const parser = document.createElement('textarea');
    parser.innerHTML = decoded;
    return parser.value.trim();
  }

  private normalizeHtml(html: string): string {
    if (!html) return '';

    let decoded = html.replace(/\uFFFD/g, '').replace(/\\n/g, '\n');

    if (!decoded.includes('<meta charset="UTF-8">')) {
      decoded = decoded.replace(/<head>/i, '<head><meta charset="UTF-8">');
    }

    return decoded;
  }

  private setupScrollTracking(): void {
    const contentElement = this.eulaContentRef.nativeElement;

    if (contentElement.scrollHeight <= contentElement.clientHeight) {
      this.hasReadFullDocument = true;
      return;
    }
    fromEvent(contentElement, 'scroll')
      .pipe(throttleTime(50), takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkScrollPosition();
      });

    this.checkScrollPosition();
  }

  private checkScrollPosition(): void {
    const element = this.eulaContentRef.nativeElement;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    const scrollThreshold = 50;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - scrollThreshold;

    if (isAtBottom && !this.hasReadFullDocument) {
      this.hasReadFullDocument = true;
    }
  }
}
